import React, { useEffect, useRef, useState } from 'react';
import { Loader } from '@googlemaps/js-api-loader';
import { Loader as LoaderIcon } from 'lucide-react';
import type { Location } from '../types';

interface MapProps {
  locations: Location[];
  selectedLocationId?: string;
  onLocationSelect?: (locationId: string) => void;
}

export default function Map({ locations, selectedLocationId, onLocationSelect }: MapProps) {
  const mapRef = useRef<HTMLDivElement>(null);
  const [map, setMap] = useState<google.maps.Map | null>(null);
  const [markers, setMarkers] = useState<google.maps.Marker[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loader = new Loader({
      apiKey: import.meta.env.VITE_GOOGLE_MAPS_API_KEY,
      version: 'weekly',
    });

    loader.load().then(() => {
      if (!mapRef.current) return;

      try {
        // Create map centered on the first location or default to US center
        const defaultCenter = { lat: 39.8283, lng: -98.5795 }; // Center of US
        const firstLocation = locations[0];
        const center = firstLocation
          ? { lat: Number(firstLocation.latitude), lng: Number(firstLocation.longitude) }
          : defaultCenter;

        const mapInstance = new google.maps.Map(mapRef.current, {
          center,
          zoom: firstLocation ? 12 : 4,
          styles: [
            {
              featureType: 'all',
              elementType: 'all',
              stylers: [{ hue: '#e7ecf0' }]
            },
            {
              featureType: 'poi',
              elementType: 'labels',
              stylers: [{ visibility: 'off' }]
            }
          ],
          mapTypeControl: false,
          streetViewControl: false,
          fullscreenControl: true,
          zoomControl: true,
          gestureHandling: 'cooperative'
        });

        setMap(mapInstance);
      } catch (err) {
        console.error('Error initializing map:', err);
        setError('Failed to initialize map. Please try again later.');
      } finally {
        setIsLoading(false);
      }
    }).catch((err) => {
      console.error('Error loading Google Maps:', err);
      setError('Failed to load Google Maps. Please check your internet connection and try again.');
      setIsLoading(false);
    });
  }, []);

  useEffect(() => {
    if (!map) return;

    try {
      // Clear existing markers
      markers.forEach(marker => marker.setMap(null));

      // Create new markers
      const newMarkers = locations.map(location => {
        const marker = new google.maps.Marker({
          position: { 
            lat: Number(location.latitude), 
            lng: Number(location.longitude) 
          },
          map,
          title: location.address,
          icon: {
            path: google.maps.SymbolPath.CIRCLE,
            scale: 10,
            fillColor: location.id === selectedLocationId ? '#9333EA' : '#E9D5FF',
            fillOpacity: 0.9,
            strokeColor: '#ffffff',
            strokeWeight: 2,
          },
          animation: google.maps.Animation.DROP,
          optimized: true
        });

        // Add info window with custom styling
        const infoWindow = new google.maps.InfoWindow({
          content: `
            <div class="p-3 max-w-xs">
              <p class="font-semibold text-gray-900 mb-1">${location.address}</p>
              <p class="text-gray-600">${location.city}, ${location.state} ${location.zip}</p>
            </div>
          `,
          pixelOffset: new google.maps.Size(0, -5),
          maxWidth: 300
        });

        marker.addListener('click', () => {
          // Close all other info windows first
          newMarkers.forEach(m => {
            if (m !== marker) {
              google.maps.event.trigger(m, 'mouseout');
            }
          });
          
          onLocationSelect?.(location.id);
          
          // Smooth pan to marker
          map.panTo(marker.getPosition()!);
        });

        let hoverTimeout: number;

        marker.addListener('mouseover', () => {
          clearTimeout(hoverTimeout);
          infoWindow.open(map, marker);
          
          // Scale up the marker
          const icon = marker.getIcon() as google.maps.Symbol;
          marker.setIcon({
            ...icon,
            scale: 12
          });
        });

        marker.addListener('mouseout', () => {
          hoverTimeout = window.setTimeout(() => {
            infoWindow.close();
            
            // Scale down the marker
            const icon = marker.getIcon() as google.maps.Symbol;
            marker.setIcon({
              ...icon,
              scale: 10
            });
          }, 100);
        });

        return marker;
      });

      setMarkers(newMarkers);

      // Fit bounds to show all markers
      if (newMarkers.length > 0) {
        const bounds = new google.maps.LatLngBounds();
        newMarkers.forEach(marker => bounds.extend(marker.getPosition()!));
        map.fitBounds(bounds);

        // Don't zoom in too close if there's only one marker
        if (newMarkers.length === 1) {
          map.setZoom(14);
        }
      }
    } catch (err) {
      console.error('Error updating markers:', err);
      setError('Failed to update map markers. Please try again later.');
    }
  }, [map, locations, selectedLocationId]);

  if (error) {
    return (
      <div className="relative w-full h-[400px] rounded-lg overflow-hidden bg-red-50 dark:bg-red-900/20 flex items-center justify-center">
        <div className="text-center p-6">
          <p className="text-red-600 dark:text-red-400 mb-2">{error}</p>
          <button 
            onClick={() => window.location.reload()} 
            className="text-red-600 dark:text-red-400 underline hover:text-red-700 dark:hover:text-red-300"
          >
            Reload page
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="relative w-full h-[400px] rounded-lg overflow-hidden shadow-md">
      {isLoading && (
        <div className="absolute inset-0 bg-gray-100 dark:bg-dark-secondary flex items-center justify-center">
          <LoaderIcon className="w-8 h-8 text-purple-600 dark:text-purple-400 animate-spin" />
          <span className="ml-2 text-gray-600 dark:text-gray-300">Loading map...</span>
        </div>
      )}
      <div ref={mapRef} className="w-full h-full" />
    </div>
  );
}